import { z } from 'zod';

// No endpoint sends an arbitrary email: these routes read the log, render templates, check lists
// and record unsubscribes.

export const MessageStatus = z.enum(['sent', 'not_sent', 'failed', 'suppressed']);
export type MessageStatus = z.infer<typeof MessageStatus>;

export const MessageKind = z.enum(['transactional', 'bulk']);
export type MessageKind = z.infer<typeof MessageKind>;

export const SuppressionScope = z.enum(['bulk', 'all']);
export type SuppressionScope = z.infer<typeof SuppressionScope>;

/** Pasted text (one per line, or separated by commas, semicolons or spaces) or a list; parsed by the service. */
export const EmailListInput = z.union([z.string(), z.array(z.string())]);
export type EmailListInput = z.input<typeof EmailListInput>;

/** page and limit are clamped by the service (limit 1–200). */
export const MessageListQuery = z.strictObject({
  status: z.union([MessageStatus, z.literal('')]).optional(),
  templateKey: z.string().optional(),
  q: z.string().optional(),
  subjectType: z.string().optional(),
  subjectId: z.string().optional(),
  page: z.coerce.number().int().optional(),
  limit: z.coerce.number().int().optional(),
});
export type MessageListQuery = z.input<typeof MessageListQuery>;

/** An unknown template is a 404 and a missing variable a 400, from the service. */
export const MessagePreviewRequest = z.strictObject({
  templateKey: z.string(),
  vars: z.record(z.string(), z.unknown()).nullable().optional(),
});
export type MessagePreviewRequest = z.input<typeof MessagePreviewRequest>;

export const RecipientsCheckRequest = z.strictObject({
  emails: EmailListInput,
  kind: MessageKind.default('bulk'),
});
export type RecipientsCheckRequest = z.input<typeof RecipientsCheckRequest>;

/** includeLifted=1 lists lifted rows too; any other value does not. */
export const SuppressionListQuery = z.strictObject({
  includeLifted: z.string().optional(),
  q: z.string().optional(),
});
export type SuppressionListQuery = z.input<typeof SuppressionListQuery>;

/** A dry run unless dryRun is false. A list with no address in it is refused by the service. */
export const SuppressRequest = z.strictObject({
  emails: EmailListInput,
  scope: SuppressionScope.default('bulk'),
  reason: z.string().nullable().default(null),
  dryRun: z.boolean().default(true),
});
export type SuppressRequest = z.input<typeof SuppressRequest>;

export const SuppressionIdParams = z.strictObject({ id: z.guid() });
export type SuppressionIdParams = z.input<typeof SuppressionIdParams>;

/** One logged email (dates as ISO strings). The body is personal data: operators with write access only. */
export const MessageLogEntry = z.object({
  id: z.string(),
  templateKey: z.string(),
  templateVersion: z.number(),
  kind: MessageKind,
  toEmail: z.string(),
  toName: z.string().nullable(),
  subject: z.string(),
  bodyText: z.string().nullable(),
  status: MessageStatus,
  reason: z.string().nullable(),
  subjectType: z.string().nullable(),
  subjectId: z.string().nullable(),
  createdBy: z.string().nullable(),
  sentAt: z.string().nullable(),
  createdAt: z.string(),
  updatedAt: z.string(),
});
export type MessageLogEntry = z.infer<typeof MessageLogEntry>;

export const MessagePage = z.object({
  messages: z.array(MessageLogEntry),
  total: z.number(),
  page: z.number(),
  limit: z.number(),
  outbox: z.boolean(),
});
export type MessagePage = z.infer<typeof MessagePage>;

export const RenderedMessage = z.object({ subject: z.string(), text: z.string(), html: z.string() });
export type RenderedMessage = z.infer<typeof RenderedMessage>;

export const MessageTemplateInfo = z.object({
  key: z.string(),
  version: z.number(),
  kind: MessageKind,
  audience: z.string(),
  description: z.string().optional(),
  bilingual: z.boolean(),
  sample: z.record(z.string(), z.string()),
  preview: RenderedMessage.nullable(),
});
export type MessageTemplateInfo = z.infer<typeof MessageTemplateInfo>;

export const MessageTemplatesResponse = z.object({ templates: z.array(MessageTemplateInfo), outbox: z.boolean() });
export type MessageTemplatesResponse = z.infer<typeof MessageTemplatesResponse>;

export const RecipientsCheck = z.object({
  kind: MessageKind,
  allowed: z.array(z.string()),
  suppressed: z.array(z.object({ email: z.string(), scope: SuppressionScope })),
  invalid: z.array(z.string()),
});
export type RecipientsCheck = z.infer<typeof RecipientsCheck>;

/** One unsubscribe (dates as ISO strings); lifting keeps the row. */
export const Suppression = z.object({
  id: z.string(),
  email: z.string(),
  scope: SuppressionScope,
  reason: z.string().nullable(),
  source: z.string(),
  createdBy: z.string().nullable(),
  liftedAt: z.string().nullable(),
  liftedBy: z.string().nullable(),
  createdAt: z.string(),
  updatedAt: z.string(),
});
export type Suppression = z.infer<typeof Suppression>;

export const SuppressResult = z.object({
  scope: SuppressionScope,
  dryRun: z.boolean(),
  toAdd: z.array(z.string()),
  toRestore: z.array(z.string()),
  toRaise: z.array(z.string()),
  already: z.array(z.string()),
  invalid: z.array(z.string()),
  changed: z.number(),
});
export type SuppressResult = z.infer<typeof SuppressResult>;
